import { Navigate, createRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { getSessionResumePath } from '@/hooks/use-wizard-navigation';
import { logger } from '@/lib/logger';
import {
  findMilestoneByRoute,
  getCompletedMilestoneIds,
  getFirstIncompleteMilestone,
  getWizardVariant,
  isRouteInAnyVariant,
  isRouteInVariant,
  isSkippingAhead,
} from '@/lib/wizard/wizard';
import type { RoutePath } from '@/lib/wizard/wizard';
import { FALLBACK_WIZARD_VARIANT_ID } from '@/lib/wizard/wizard.config';
import { ForMe } from '@/views/create-profile/for-me/ForMe';
import { ForSomeoneElse } from '@/views/create-profile/for-someone-else/ForSomeoneElse';
import { Relationship } from '@/views/create-profile/for-someone-else/Relationship';
import { Shipping } from '@/views/create-profile/for-someone-else/Shipping';
import { InsuranceBenefits as RevampInsuranceBenefits } from '@/views/insurance/flows/eligibility-revamp/InsuranceBenefits';
import { InsuranceDiscoveryPending as RevampInsuranceDiscoveryPending } from '@/views/insurance/flows/eligibility-revamp/InsuranceDiscoveryPending';
import { InsuranceResolve as RevampInsuranceResolve } from '@/views/insurance/flows/eligibility-revamp/InsuranceResolve';
import { InsuranceUpload as RevampInsuranceUpload } from '@/views/insurance/flows/eligibility-revamp/InsuranceUpload';
import { InsuranceVerifying as RevampInsuranceVerifying } from '@/views/insurance/flows/eligibility-revamp/InsuranceVerifying';
import { InsuranceLinkSent } from '@/views/insurance/flows/eligibility-revamp/terminal/send-link/InsuranceLinkSent';
import { InsuranceSendLink } from '@/views/insurance/flows/eligibility-revamp/terminal/send-link/InsuranceSendLink';
import { InsuranceBenefits as LegacyInsuranceBenefits } from '@/views/insurance/flows/legacy/InsuranceBenefits';
import LegacyInsuranceDiscoveryPending from '@/views/insurance/flows/legacy/InsuranceDiscoveryPending';
import { InsuranceFinalize as LegacyInsuranceFinalize } from '@/views/insurance/flows/legacy/InsuranceFinalize';
import LegacyInsuranceInput from '@/views/insurance/flows/legacy/InsuranceInput';
import LegacyInsuranceInputConfirm from '@/views/insurance/flows/legacy/InsuranceInputConfirm';
import { InsuranceOptions as LegacyInsuranceOptions } from '@/views/insurance/flows/legacy/InsuranceOptions';
import { InsurancePreDiscovery as LegacyInsurancePreDiscovery } from '@/views/insurance/flows/legacy/InsurancePreDiscovery';
import LegacyInsuranceVerifyPending from '@/views/insurance/flows/legacy/InsuranceVerifyPending';
import { InsuranceFollowUp as LegacyInsuranceFollowUp } from '@/views/insurance/flows/legacy/terminal/follow-up-reach-out/InsuranceFollowUp';
import { InsuranceReachOut as LegacyInsuranceReachOut } from '@/views/insurance/flows/legacy/terminal/follow-up-reach-out/InsuranceReachOut';
import {
  InsuranceEligibilityFailReasonSchema,
  InsuranceIneligible,
} from '@/views/insurance/terminal/ineligible-waitlist/InsuranceIneligible';
import { InsuranceWaitlistThankYou } from '@/views/insurance/terminal/ineligible-waitlist/InsuranceWaitlistThankYou';
import { DisplayOrder } from '@/views/order/DisplayOrder.tsx';
import { PatientInformation } from '@/views/patient-information/PatientInformation.tsx';
import { ProductCategorySelection } from '@/views/product-category-selection/ProductCategorySelection.tsx';
import { SelectedProductPrimer } from '@/views/product-category-selection/SelectedProductPrimer';
import { NoProductsSelected } from '@/views/products/NoProductsSelected';
import ProductSummary from '@/views/products/ProductSummary.tsx';
import SelectAccessibility from '@/views/products/SelectAccessibility.tsx';
import SelectDiabetes from '@/views/products/SelectDiabetes.tsx';
import SelectIncontinence from '@/views/products/SelectIncontinence.tsx';
import SelectMobility from '@/views/products/SelectMobility.tsx';
import SelectNutrition from '@/views/products/SelectNutrition.tsx';
import { RescheduleAppointment } from '@/views/reschedule-appointment/RescheduleAppointment';
import { ScheduleAppointment } from '@/views/schedule-appointment/ScheduleAppointment';
import { TelehealthExplainer } from '@/views/schedule-appointment/TelehealthExplainer';
import { SelfScheduleAppointment } from '@/views/self-schedule-appointment/SelfScheduleAppointment';
import { SessionResume } from '@/views/session/SessionResume';
import { StartPhoneVerification } from '@/views/verify-profile/StartPhoneVerification';
import { VerifyPhoneCode } from '@/views/verify-profile/VerifyPhoneCode';

import { BaseLayout } from './components/BaseLayout';
import { EntryBaseLayout } from './components/EntryBaseLayout';
import { RootLayout } from './components/RootLayout';
import { rootRoute } from './root-route';
import { showSinkRoute, sinkRouteTree } from './sink-routes';

const carrierTypeSchema = z.enum([
  'medicare',
  'medicare_advantage',
  'medicaid',
  'medicaid_managed_care',
]);
const planFamilyParamSchema = z.enum(['medicare', 'medicaid']);
const resolveOverrideSchema = z.enum(['ask_all', 'ask_managed']);
const commonInsuranceNavigationSearchSchema = z.object({
  override: resolveOverrideSchema.optional(),
  previousPlanFamily: planFamilyParamSchema.optional(),
  preserveLaterFamilies: z.enum(['true']).optional(),
});

const legacyCarrierTypeSchema = z.object({
  carrierType: z
    .enum(['medicare', 'medicare_advantage', 'medicaid', 'medicaid_managed_care'])
    .optional(),
});

const planFamilySchema = commonInsuranceNavigationSearchSchema.extend({
  planFamily: planFamilyParamSchema.optional(),
  previousUploadPlanType: carrierTypeSchema.optional(),
});

const planTypeSchema = commonInsuranceNavigationSearchSchema.extend({
  planType: carrierTypeSchema.optional(),
});

const verificationSearchSchema = commonInsuranceNavigationSearchSchema.extend({
  previousUploadPlanType: carrierTypeSchema.optional(),
});

const sendLinkSearchSchema = commonInsuranceNavigationSearchSchema.extend({
  planFamily: planFamilyParamSchema.optional(),
  planType: carrierTypeSchema.optional(),
  previousUploadPlanType: carrierTypeSchema.optional(),
});

const ineligibleReasonSchema = z.object({
  reason: InsuranceEligibilityFailReasonSchema,
});

// TODO: Type this properly so its not just a general string
const productNextStepSchema = z.object({
  nextStep: z.string().optional(),
});

const sessionResumeSearchSchema = z.object({
  token: z.string(),
});
const selectedProductPrimerSearchSchema = z.object({
  product: z.string().optional(),
});
const referrerProductIdSchema = z.uuid();

const rescheduleAppointmentSearchSchema = z.object({
  appointmentId: z
    .string()
    .or(z.number())
    .optional()
    .describe(
      'The ID of the appointment to reschedule, if absent, the patient will need to identify themselves'
    ),
  appointmentTypeId: z
    .number()
    .or(z.string())
    .describe('The Healthie appointment type ID for the new appointment'),
});

function useEligibilityRevampEnabled() {
  return useSessionStore((state) => state.wizardVariantId === 'eligibility-revamp');
}

function InsuranceDiscoveryPending() {
  const isEligibilityRevamp = useEligibilityRevampEnabled();
  return isEligibilityRevamp ? (
    <RevampInsuranceDiscoveryPending />
  ) : (
    <LegacyInsuranceDiscoveryPending />
  );
}

function InsuranceBenefits() {
  const isEligibilityRevamp = useEligibilityRevampEnabled();
  return isEligibilityRevamp ? <RevampInsuranceBenefits /> : <LegacyInsuranceBenefits />;
}

const rootLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'appLayout',
  component: RootLayout,
  beforeLoad: ({ location }) => {
    const sessionState = useSessionStore.getState();
    const path = location.pathname as RoutePath;
    const variant = getWizardVariant(sessionState.wizardVariantId ?? FALLBACK_WIZARD_VARIANT_ID);

    const disableRouteSkipAheadValidation =
      import.meta.env.VITE_DISABLE_ROUTE_SKIP_AHEAD_VALIDATION === 'true';

    if (isRouteInAnyVariant(path) && !isRouteInVariant(variant, path)) {
      const resumePath = getSessionResumePath(sessionState);
      logger.error(new Error('Route does not belong to active wizard variant'), {
        location: path,
        resumePath,
        wizard_variant_id: sessionState.wizardVariantId,
      });

      throw redirect({ to: resumePath, replace: true });
    }

    if (!disableRouteSkipAheadValidation && isSkippingAhead(path, sessionState)) {
      const resumePath = getSessionResumePath(sessionState);

      // Capture detailed debug info for investigating skip-ahead redirects
      const targetMilestone = findMilestoneByRoute(variant, path);
      const firstIncomplete = getFirstIncompleteMilestone(sessionState, variant.milestones);
      const completedIds = getCompletedMilestoneIds(sessionState, variant.milestones);

      logger.error(new Error('Skipping ahead validation'), {
        location: path,
        resumePath,
        debug: {
          attempted_path: path,
          redirect_path: resumePath,
          target_milestone_id: targetMilestone?.id ?? null,
          first_incomplete_milestone_id: firstIncomplete?.id ?? null,
          completed_milestone_ids: completedIds,
          wizard_variant_id: sessionState.wizardVariantId,

          // Include key state fields that affect milestone completion
          milestone_state: {
            has_categories: sessionState.selectedCategoryIds.length > 0,
            has_patient_info: Boolean(
              sessionState.patientRecipient &&
              sessionState.patientFirstName &&
              sessionState.patientLastName &&
              sessionState.patientDateOfBirth &&
              sessionState.patientGender
            ),
            insurance_builder_resolved_families: sessionState.insuranceBuilder.resolvedFamilies,
            insurance_builder_covered_families: sessionState.insuranceBuilder.coveredFamilies,
            insurance_builder_ran_verification: sessionState.insuranceBuilder.ranVerification,
            insurance_builder_verification_retry_attempted:
              sessionState.insuranceBuilder.verificationRetryAttempted,
            has_contact_info: Boolean(
              sessionState.contactEmail &&
              sessionState.contactPhoneNumber &&
              sessionState.contactAddressLine1
            ),
            product_selection_confirmed: sessionState.productSelectionConfirmed,
            has_scheduling_slot: Boolean(sessionState.schedulingSelectedSlot),
            phone_verified: sessionState.contactPhoneNumberVerified,
            exit_milestone: sessionState.exitMilestone,
          },
        },
      });

      throw redirect({ to: resumePath, replace: true });
    }
  },
});

// Pathless layout routes split the standard wizard chrome from the entry-screen
// chrome (background, header shadow, main width, content margins) so each layout
// is modeled directly in the route tree instead of branching at runtime.
const entryLayoutRoute = createRoute({
  getParentRoute: () => rootLayoutRoute,
  id: 'entryLayout',
  component: EntryBaseLayout,
});

const wizardLayoutRoute = createRoute({
  getParentRoute: () => rootLayoutRoute,
  id: 'wizardLayout',
  component: BaseLayout,
});

// ============================================================================
// Entry & Initial Flow Routes
// ============================================================================

const indexRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/',
  component: () => <Navigate to="/0" replace />,
});

const productCategoryRoute = createRoute({
  getParentRoute: () => entryLayoutRoute,
  path: '/0',
  component: ProductCategorySelection,
});

const selectedProductPrimerRoute = createRoute({
  getParentRoute: () => entryLayoutRoute,
  path: '/start',
  component: SelectedProductPrimer,
  validateSearch: selectedProductPrimerSearchSchema,
  // Validate the id here rather than in validateSearch so a malformed `product`
  // redirects to /0 instead of throwing a search-validation error.
  beforeLoad: ({ search }) => {
    const productId = referrerProductIdSchema.safeParse(search.product);

    if (!productId.success) {
      // Clear any stale referrer so a visit without a valid product is treated
      // as a direct entry rather than inheriting a prior product's context.
      useSessionStore.getState().updateState({ referrerProductId: null });
      throw redirect({ to: '/0', replace: true });
    }

    const { referrerProductId, updateState } = useSessionStore.getState();
    const isNewReferrerProduct = referrerProductId !== productId.data;

    updateState({
      referrerProductId: productId.data,
      // Drop prior categories on a new referrer so /0 doesn't merge unrelated ones.
      ...(isNewReferrerProduct ? { selectedCategoryIds: [] } : {}),
    });
  },
});

const patientInformationRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/1',
  component: PatientInformation,
});

// ============================================================================
// Insurance Flow Routes
// ============================================================================

const insuranceDiscoveryPendingRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/discovery-pending',
  component: InsuranceDiscoveryPending,
});

const insurancePreDiscoveryRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/pre-discovery',
  component: LegacyInsurancePreDiscovery,
});

const insuranceOptionsRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/options',
  component: LegacyInsuranceOptions,
});

const insuranceInputRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/input',
  component: LegacyInsuranceInput,
  validateSearch: legacyCarrierTypeSchema,
});

const insuranceInputConfirmRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/input-confirm',
  component: LegacyInsuranceInputConfirm,
  validateSearch: legacyCarrierTypeSchema,
});

const insuranceVerifyPendingRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/verify-pending',
  component: LegacyInsuranceVerifyPending,
});

const insuranceFinalizeRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/finalize',
  component: LegacyInsuranceFinalize,
});

const insuranceResolveRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/resolve',
  component: RevampInsuranceResolve,
  validateSearch: planFamilySchema,
});

const insuranceUploadRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/upload',
  component: RevampInsuranceUpload,
  validateSearch: planTypeSchema,
});

const insuranceVerifyingRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/verifying',
  component: RevampInsuranceVerifying,
  validateSearch: verificationSearchSchema,
});

const insuranceBenefitsRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/benefits',
  component: InsuranceBenefits,
});

// ============================================================================
// Insurance Terminal Routes (Deadend Branches)
// ============================================================================

// Deadend Branch: Ineligible -> Waitlist
const insuranceIneligibleRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/ineligible',
  component: InsuranceIneligible,
  validateSearch: ineligibleReasonSchema,
});
const insuranceWaitlistThankYouRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/waitlist/thank-you',
  component: InsuranceWaitlistThankYou,
});

// Deadend Branch: Follow-up -> Reach Out
const insuranceFollowUpRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/follow-up',
  component: LegacyInsuranceFollowUp,
});
const insuranceReachOutRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/reach-out',
  component: LegacyInsuranceReachOut,
});

// Deadend Branch: Send Link -> Link Sent
const insuranceSendLinkRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/send-link',
  component: InsuranceSendLink,
  validateSearch: sendLinkSearchSchema,
});
const insuranceLinkSentRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/insurance/link-sent',
  component: InsuranceLinkSent,
});

// ============================================================================
// Product Selection Routes
// ============================================================================

const selectAccessibilityRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'products/accessibility',
  component: SelectAccessibility,
  validateSearch: productNextStepSchema,
});

const selectDiabetesRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'products/diabetes',
  component: SelectDiabetes,
  validateSearch: productNextStepSchema,
});

const selectIncontinenceRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'products/incontinence',
  component: SelectIncontinence,
  validateSearch: productNextStepSchema,
});

const selectMobilityRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'products/mobility',
  component: SelectMobility,
  validateSearch: productNextStepSchema,
});

const selectNutritionRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'products/nutrition',
  component: SelectNutrition,
  validateSearch: productNextStepSchema,
});

const noProductsSelectedRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'products/no-products-selected',
  component: NoProductsSelected,
});

const productSummaryRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'products/summary',
  component: ProductSummary,
});

// ============================================================================
// Profile Creation Routes
// ============================================================================

const forMeRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'create-profile/for-me',
  component: ForMe,
});

const forSomeoneElseRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'create-profile/for-someone-else',
  component: ForSomeoneElse,
});

const relationshipRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'create-profile/for-someone-else/relationship',
  component: Relationship,
});

const shippingRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'create-profile/for-someone-else/shipping',
  component: Shipping,
});

// ============================================================================
// Appointment Routes
// ============================================================================

const telehealthExplainerRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'appointment/intro',
  component: TelehealthExplainer,
});

const scheduleAppointmentRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'appointment/book-call',
  component: ScheduleAppointment,
});

const rescheduleAppointmentRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'appointment/reschedule',
  component: RescheduleAppointment,
  validateSearch: rescheduleAppointmentSearchSchema,
  beforeLoad: ({ search }) => {
    // Redirect to home if appointment type ID is not provided.
    if (!search.appointmentTypeId) {
      throw redirect({ to: '/', replace: true });
    }
  },
});

const selfScheduleAppointmentRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'appointment/schedule/$orderPublicId',
  component: SelfScheduleAppointment,
});

// ============================================================================
// Verification Routes
// ============================================================================

const startPhoneVerificationRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'verify-profile/phone-number',
  component: StartPhoneVerification,
});

const verifyPhoneCodeRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: 'verify-profile/phone-number/code',
  component: VerifyPhoneCode,
});

// ============================================================================
// Session Routes
// ============================================================================

const sessionResumeRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/session/resume',
  component: SessionResume,
  validateSearch: sessionResumeSearchSchema,
  beforeLoad: ({ search }) => {
    if (!search.token) {
      throw redirect({ to: '/', replace: true });
    }
  },
});

// ============================================================================
// Order Completion Routes (Terminal)
// ============================================================================

const displayOrderRoute = createRoute({
  getParentRoute: () => wizardLayoutRoute,
  path: '/appointment/confirmed',
  component: DisplayOrder,
});

export const routeTree = rootRoute.addChildren([
  rootLayoutRoute.addChildren([
    entryLayoutRoute.addChildren([productCategoryRoute, selectedProductPrimerRoute]),
    wizardLayoutRoute.addChildren([
      indexRoute,
      displayOrderRoute,
      forMeRoute,
      forSomeoneElseRoute,
      insuranceBenefitsRoute,
      insuranceDiscoveryPendingRoute,
      insuranceFinalizeRoute,
      insuranceFollowUpRoute,
      insuranceIneligibleRoute,
      insuranceInputConfirmRoute,
      insuranceInputRoute,
      insuranceLinkSentRoute,
      insuranceOptionsRoute,
      insurancePreDiscoveryRoute,
      insuranceReachOutRoute,
      insuranceResolveRoute,
      insuranceSendLinkRoute,
      insuranceUploadRoute,
      insuranceVerifyPendingRoute,
      insuranceVerifyingRoute,
      insuranceWaitlistThankYouRoute,
      noProductsSelectedRoute,
      patientInformationRoute,
      productSummaryRoute,
      relationshipRoute,
      rescheduleAppointmentRoute,
      scheduleAppointmentRoute,
      selectAccessibilityRoute,
      selectDiabetesRoute,
      selectIncontinenceRoute,
      selectMobilityRoute,
      selectNutritionRoute,
      selfScheduleAppointmentRoute,
      sessionResumeRoute,
      shippingRoute,
      startPhoneVerificationRoute,
      telehealthExplainerRoute,
      verifyPhoneCodeRoute,
    ]),
  ]),
  ...(showSinkRoute ? [sinkRouteTree] : []),
]);
